import { useEffect, useState, type FormEvent } from "react";
import { api } from "./lib/api.ts";
import { useData } from "./state/store.tsx";
import type { Notification, RunItem } from "@shared/types.ts";
import { SkeletonCard } from "./components/ui.tsx";
import { NotificationBell } from "./components/NotificationBell.tsx";
import Demand from "./views/Demand.tsx";
import Booking from "./views/Booking.tsx";
import Ordering from "./views/Ordering.tsx";
import Receiving from "./views/Receiving.tsx";
import Stock from "./views/Stock.tsx";
import Production from "./views/Production.tsx";
import BatchSheetView from "./views/BatchSheetView.tsx";
import Cogs from "./views/Cogs.tsx";
import Distribution from "./views/Distribution.tsx";
import Admin from "./views/Admin.tsx";

type Area = "demand" | "booking" | "ordering" | "receiving" | "stock" | "production" | "batch" | "cogs" | "distribution" | "admin";
const AREAS: { id: Area; label: string }[] = [
  { id: "demand", label: "Demand Planning" },
  { id: "booking", label: "Booking" },
  { id: "ordering", label: "Ingredient Ordering" },
  { id: "receiving", label: "Receiving" },
  { id: "stock", label: "Raw Materials" },
  { id: "production", label: "Production" },
  { id: "batch", label: "Batch Sheet" },
  { id: "cogs", label: "COGS" },
  { id: "distribution", label: "Distribution" },
  { id: "admin", label: "Admin & Setup" },
];

export default function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const { reload } = useData();

  useEffect(() => {
    api.authStatus().then((r) => setAuthed(r.authed)).catch(() => setAuthed(false));
  }, []);

  if (authed === null) {
    return <div className="min-h-screen grid place-items-center text-muted text-sm">Loading…</div>;
  }
  if (!authed) return <Login onLogin={() => { setAuthed(true); void reload(); }} />;
  return <Shell />;
}

function Login({ onLogin }: { onLogin: () => void }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.login(pw);
      onLogin();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Login failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen grid place-items-center p-4">
      <form onSubmit={submit} className="card w-full max-w-sm space-y-4">
        <div>
          <h1 className="text-lg font-semibold">Manufacturing</h1>
          <p className="text-muted text-sm mt-1">Enter the owner password to continue.</p>
        </div>
        <input autoFocus type="password" className="input" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} />
        {err && <div className="text-danger text-sm">{err}</div>}
        <button className="btn-primary w-full" disabled={busy || !pw}>{busy ? "…" : "Unlock"}</button>
      </form>
    </div>
  );
}

// Where each notification kind lives for the owner — so a note opens the exact screen it's about.
const AREA_FOR_KIND: Record<string, Area> = {
  reorder: "demand",          // "time to book production" → demand planning
  received: "receiving",      // an ingredient landed → receiving
  missing: "receiving",       // ingredients short → chase the order in receiving
  date_proposed: "production",
  date_confirmed: "production",
  ingredients_in: "production",
  containers_in: "production",
  started: "production",
  produced: "production",
  shipped: "production",
  delay: "production",
  stale: "production",
};

function Shell() {
  const { registry, data, businessId, switchBusiness, loading, error, saving, reload } = useData();
  const [area, setArea] = useState<Area>("demand");
  const [pendingItems, setPendingItems] = useState<RunItem[] | null>(null);
  const [focusRun, setFocusRun] = useState<string | null>(null);

  const bookItems = (items: RunItem[]) => { setPendingItems(items); setArea("booking"); };

  // Open a notification → jump to its section, and scroll/highlight the run it's about.
  const openNotif = (n: Notification) => {
    setArea(AREA_FOR_KIND[n.kind] || (n.runId ? "production" : "demand"));
    setFocusRun(n.runId || null);
    if (n.runId) window.setTimeout(() => setFocusRun(null), 4000);
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-panel/90 backdrop-blur border-b border-edge">
        <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <div className="font-semibold text-ink shrink-0">Manufacturing</div>
          <select
            className="input w-auto py-1.5 text-sm max-w-[16rem]"
            value={businessId}
            onChange={(e) => { if (e.target.value === "__new") { setArea("admin"); } else switchBusiness(e.target.value); }}
          >
            {registry.businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            {registry.businesses.length === 0 && <option value="">No business yet</option>}
          </select>
          <div className="flex-1" />
          {saving && <span className="text-xs text-muted">Saving…</span>}
          {data && (
            <NotificationBell
              notifications={(data.business.notifications || []).filter((n) => n.audience === "owner")}
              onMarkRead={async (ids) => { await api.notifRead({ businessId: data.business.id, ids }); await reload(); }}
              onOpen={openNotif}
            />
          )}
          <button className="btn-sm border border-edge hover:bg-paper" onClick={async () => { await api.logout(); location.reload(); }}>Log out</button>
        </div>
        <div className="max-w-6xl mx-auto px-4 pb-2 overflow-x-auto no-scrollbar">
          <nav className="flex gap-1">
            {AREAS.map((a) => (
              <button key={a.id} onClick={() => setArea(a.id)}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition ${area === a.id ? "bg-brand text-white" : "text-muted hover:text-ink hover:bg-paper"}`}>
                {a.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-5">
        {error && <div className="card border-danger/30 bg-danger/5 text-danger text-sm mb-4">{error}</div>}
        {loading && !data ? (
          <div className="space-y-4"><SkeletonCard /><SkeletonCard /></div>
        ) : !data ? (
          <NoBusiness />
        ) : (
          <>
            {area === "demand" && <Demand onBook={bookItems} />}
            {area === "booking" && <Booking initialItems={pendingItems} onConsumeInitial={() => setPendingItems(null)} />}
            {area === "ordering" && <Ordering />}
            {area === "receiving" && <Receiving focusRunId={focusRun} />}
            {area === "stock" && <Stock />}
            {area === "production" && <Production focusRunId={focusRun} />}
            {area === "batch" && <BatchSheetView />}
            {area === "cogs" && <Cogs />}
            {area === "distribution" && <Distribution />}
            {area === "admin" && <Admin />}
          </>
        )}
      </main>
    </div>
  );
}

function NoBusiness() {
  const { seedLittleRick, saving } = useData();
  return (
    <div className="card text-center py-12">
      <div className="text-sm font-medium">No business loaded yet</div>
      <p className="text-muted text-sm mt-1">Import the Little Rick — US dataset to get started, or create a new business in Admin.</p>
      <button className="btn-primary mt-4" disabled={saving} onClick={() => void seedLittleRick()}>{saving ? "Importing…" : "Import Little Rick — US"}</button>
    </div>
  );
}
